import { AsyncFunction } from "src/types/functions";
import { AsyncRender, AsyncRenderProps } from "./AsyncRender";
import { useAsyncFetch } from "src/utils/async/asyncFetch.ts";
import { RefreshableElementProps, setRefreshRefValue } from "src/utils/useRefreshRef.ts";
import React from "react";
import { AsyncDataRefreshOptions } from "src/utils/async/asyncState";

export interface AsyncFetchRenderProps<TData, TReloadParams>
	extends RefreshableElementProps,
		Omit<AsyncRenderProps<TData, TReloadParams>, "asyncData" | "content"> {
	fetch: AsyncFunction<TReloadParams | undefined, TData>;
	defaultValue?: TData;
	content: (data: TData, reload: AsyncFetchRefreshFunc<TData, TReloadParams>) => React.ReactNode;
}

export type AsyncFetchRefreshFunc<TData, TReloadParams = undefined> = (
	options?: AsyncDataRefreshOptions,
	reloadParams?: TReloadParams,
) => Promise<TData>;

export const AsyncFetchRender = <TData, TReloadParams = undefined>({
	fetch,
	content,
	refreshRef,
	defaultValue,
	...other
}: AsyncFetchRenderProps<TData, TReloadParams>) => {
	const [asyncState, reload] = useAsyncFetch<TData, TReloadParams>(fetch, {
		defaultValue: defaultValue,
		fetchOnMount: defaultValue === undefined,
	});

	setRefreshRefValue(refreshRef, () => reload());

	return (
		<AsyncRender
			asyncData={asyncState}
			reloadOnError={(params) => reload(params)}
			content={(data) => {
				return content(data, async (reloadOptions, reloadParams) => {
					return await reload(reloadParams, reloadOptions);
				});
			}}
			{...other}
		/>
	);
};
